import React, {
  useState,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/browser';
import { useFeatureFlagEnabled } from 'posthog-js/react';

import { PillButton } from '../../../components/Button/ActionButtons';
import useSessionStore from '../../../hooks/useSessionStore';
import useComponentMounted from '../../../hooks/useComponentMounted';
import useToast from '../../hooks/useToast';
import User from '../../../Model/User';
import CreateCoachTaskModal from '../../layout/components/CreateCoachTaskModal';
import { FeatureFlag } from '../../../utils/postHog';

import {
  CircleIconButton,
  CircleTaskIcon,
  PillTaskIcon,
} from './styles';
import text from './text.json';

/**
 * Opens the create-task modal pre-filled with a client (the task's subject), from a
 * client header (the Activity Feed, Manage Clients, or Chat). The created task is
 * client-directed (`user` = the client); `coach` is the client's assigned coach so it
 * lands in that coach's feed. The client can be removed in the modal to make the task
 * coach-level instead.
 *
 * Renders nothing unless the viewer is internal staff and the subject is a real client
 * distinct from the coach (the feed reuses the same card for coach-subject rows).
 */
const AddClientTaskButton = ({
  clientId,
  coachId,
  pill,
  className,
}) => {
  const { isAdmin, isCoachAssistant } = useSessionStore();
  const { showToast } = useToast();
  const isComponentMountedRef = useComponentMounted();
  const isCoachTasksEnabled = useFeatureFlagEnabled(FeatureFlag.ENABLE_COACH_TASKS);

  const [isModalOpen, setIsModalOpen] = useState(false);
  // Mount the modal only after the first open so lists (the feed, Manage Clients)
  // that render this per row don't pay for one hidden modal — and its useStorage — each.
  const [hasOpened, setHasOpened] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // Client + coach docs resolved on open (names/avatars feed the task's header).
  const [subject, setSubject] = useState(null);
  const [coach, setCoach] = useState(null);

  const onModalDismiss = useCallback(() => {
    if (isComponentMountedRef.current) {
      setIsModalOpen(false);
    }
  }, [isComponentMountedRef]);

  const showCreatedToast = useCallback(() => {
    showToast(text.taskCreated);
  }, [showToast]);

  const showErrorToast = useCallback((errorMessage = '') => {
    showToast(errorMessage || text.taskError, { error: true });
  }, [showToast]);

  const handleOpen = useCallback(async () => {
    setIsLoading(true);
    try {
      const [clientDoc, coachDoc] = await Promise.all([
        User.getById(clientId),
        User.getById(coachId),
      ]);
      if (!isComponentMountedRef.current) {
        return;
      }
      setSubject({ id: clientId, name: clientDoc?.name || '', avatar: clientDoc?.avatarUrl || '' });
      setCoach({ id: coachId, name: coachDoc?.name || '', avatar: coachDoc?.avatarUrl || '' });
      setHasOpened(true);
      setIsModalOpen(true);
    } catch (error) {
      Sentry.captureException(error, { extra: { clientId, coachId } });
      showToast(text.loadError, { error: true });
    } finally {
      if (isComponentMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [clientId, coachId, isComponentMountedRef, showToast]);

  // Only internal staff create coach tasks, and only for a real client (the feed reuses
  // this card for coach-subject rows, where clientId === coachId).
  const canCreate = (isAdmin || isCoachAssistant) && !!clientId && !!coachId && clientId !== coachId
    && isCoachTasksEnabled;
  if (!canCreate) {
    return null;
  }

  const renderTrigger = () => {
    // Text pill, matching the other header actions in chat.
    if (pill) {
      return (
        <PillButton
          onClick={handleOpen}
          icon={<PillTaskIcon />}
          variant="info"
          size="small"
          disabled={isLoading}
          className={className}
        >
          {text.caption}
        </PillButton>
      );
    }
    return (
      <CircleIconButton
        onClick={handleOpen}
        disabled={isLoading}
        className={className}
        title={text.caption}
        aria-label={text.caption}
      >
        <CircleTaskIcon />
      </CircleIconButton>
    );
  };

  return (
    <>
      {renderTrigger()}
      {hasOpened && (
        <CreateCoachTaskModal
          isOpen={isModalOpen}
          onDidDismiss={onModalDismiss}
          onCreated={showCreatedToast}
          onError={showErrorToast}
          subjectId={subject?.id || ''}
          subjectName={subject?.name || ''}
          subjectAvatar={subject?.avatar || ''}
          coachId={coach?.id || ''}
          coachName={coach?.name || ''}
          coachAvatar={coach?.avatar || ''}
        />
      )}
    </>
  );
};

AddClientTaskButton.propTypes = {
  clientId: PropTypes.string.isRequired,
  coachId: PropTypes.string,
  // Text pill variant (chat header), matching the neighbouring action pills.
  // Defaults to a compact circle icon (the feed / Manage Clients cards).
  pill: PropTypes.bool,
  className: PropTypes.string,
};

AddClientTaskButton.defaultProps = {
  coachId: '',
  pill: false,
  className: '',
};

export default AddClientTaskButton;
